import React, {useEffect, useState} from 'react';
import {CommandDtoExecuteOptionEnum, ExecuteOptionModifierButtonEnum} from "../../../../api";
import {Form, Input, Checkbox, Select, Collapse} from "antd";

const {Option} = Select;
const {Panel} = Collapse;

// Mappings von ENUM-Werten zu Formularfeldern
const enumToInputFieldMapping: Record<CommandDtoExecuteOptionEnum, string[]> = {
    [CommandDtoExecuteOptionEnum.Click]: ['mouseButton', 'clickCount', 'delay', 'force', 'position', 'timeout', 'trial'],
    [CommandDtoExecuteOptionEnum.Dblclick]: ['mouseButton', 'clickCount', 'delay', 'force', 'position', 'timeout', 'trial'],
    [CommandDtoExecuteOptionEnum.Drag]: ['force', 'sourcePosition', 'targetPosition', 'timeout', 'trial'],
    [CommandDtoExecuteOptionEnum.Fill]: ['force', 'timeout'],
    [CommandDtoExecuteOptionEnum.Check]: ['force', 'position', 'timeout', 'trial'],
    [CommandDtoExecuteOptionEnum.Clear]: ['force', 'timeout'],
    [CommandDtoExecuteOptionEnum.Uncheck]: ['force', 'position', 'timeout', 'trial'],
    [CommandDtoExecuteOptionEnum.Press]: ['delay', 'timeout'],
    [CommandDtoExecuteOptionEnum.PressSequentially]: ['delay', 'timeout'],
    [CommandDtoExecuteOptionEnum.SelectOption]: ['force', 'timeout'],
    [CommandDtoExecuteOptionEnum.Hover]: ['force', 'position', 'timeout', 'trial'],
};

const mouseButtons = Object.values(ExecuteOptionModifierButtonEnum);

type ExecuteCommandOptionComponentProps = {
    selectedOption: CommandDtoExecuteOptionEnum; // Die ausgewählte Option wird jetzt von außen als Prop übergeben
    form: any; // Form API von Ant Design
};

const ExecuteCommandOptionComponent: React.FC<ExecuteCommandOptionComponentProps> = ({selectedOption, form}) => {
    const [currentOption, setCurrentOption] = useState<CommandDtoExecuteOptionEnum | undefined>(undefined);

    useEffect(() => {
        setCurrentOption(selectedOption);
    }, [selectedOption]);

    // Funktion zum Rendern der Input-Felder basierend auf der Auswahl
    const renderInputFields = () => {
        if (!currentOption) return null;

        const fields = enumToInputFieldMapping[currentOption];
        return fields.map((field) => {
            switch (field) {
                case 'force':
                    return (
                        <Form.Item
                            key="force"
                            name="force"
                            valuePropName="checked"
                        >
                            <Checkbox onChange={(e) => form.setFieldsValue({force: e.target.checked})}>
                                Erzwingen{' '}
                                <span style={{fontSize: '12px', color: 'gray'}}>
                                   (Befehl wird auch ohne erfolgreiches Lokalisieren des Zielelementes ausgeführt)
                                </span>
                            </Checkbox>
                        </Form.Item>
                    );
                case 'timeout':
                    return (
                        <Form.Item
                            key="timeout"
                            name="timeout"
                            label="Wartezeit (ms)"
                        >
                            <Input
                                type="number"
                                placeholder="Wartezeit in ms"
                                onChange={(e) => form.setFieldsValue({timeout: Number(e.target.value)})}
                            />
                        </Form.Item>
                    );
                case 'position':
                    return (
                        <Form.Item
                            key="position"
                            label="Position"
                        >
                            <Input.Group compact>
                                <Form.Item
                                    name={['position', 'x']} // Eingabefelder für X- und Y-Position
                                    noStyle
                                >
                                    <Input
                                        style={{width: '50%'}}
                                        placeholder="Position X"
                                        onChange={(e) => form.setFieldsValue({
                                            position: {
                                                ...form.getFieldValue('position'),
                                                x: Number(e.target.value)
                                            }
                                        })}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name={['position', 'y']} // Eingabefelder für X- und Y-Position
                                    noStyle
                                >
                                    <Input
                                        style={{width: '50%'}}
                                        placeholder="Position Y"
                                        onChange={(e) => form.setFieldsValue({
                                            position: {
                                                ...form.getFieldValue('position'),
                                                y: Number(e.target.value)
                                            }
                                        })}
                                    />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    );
                case 'sourcePosition':
                    return (
                        <Form.Item
                            key="sourcePosition"
                            label="Quell-Position"
                        >
                            <Input.Group compact>
                                <Form.Item
                                    name={['sourcePosition', 'x']} // Eingabefelder für Quell-Position X und Y
                                    noStyle
                                >
                                    <Input
                                        style={{width: '50%'}}
                                        placeholder="Quell-Position X"
                                        onChange={(e) => form.setFieldsValue({
                                            sourcePosition: {
                                                ...form.getFieldValue('sourcePosition'),
                                                x: Number(e.target.value)
                                            }
                                        })}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name={['sourcePosition', 'y']} // Eingabefelder für Quell-Position X und Y
                                    noStyle
                                >
                                    <Input
                                        style={{width: '50%'}}
                                        placeholder="Quell-Position Y"
                                        onChange={(e) => form.setFieldsValue({
                                            sourcePosition: {
                                                ...form.getFieldValue('sourcePosition'),
                                                y: Number(e.target.value)
                                            }
                                        })}
                                    />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    );
                case 'targetPosition':
                    return (
                        <Form.Item
                            key="targetPosition"
                            label="Ziel-Position"
                        >
                            <Input.Group compact>
                                <Form.Item
                                    name={['targetPosition', 'x']} // Eingabefelder für Ziel-Position X und Y
                                    noStyle
                                >
                                    <Input
                                        style={{width: '50%'}}
                                        placeholder="Ziel-Position X"
                                        onChange={(e) => form.setFieldsValue({
                                            targetPosition: {
                                                ...form.getFieldValue('targetPosition'),
                                                x: Number(e.target.value)
                                            }
                                        })}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name={['targetPosition', 'y']} // Eingabefelder für Ziel-Position X und Y
                                    noStyle
                                >
                                    <Input
                                        style={{width: '50%'}}
                                        placeholder="Ziel-Position Y"
                                        onChange={(e) => form.setFieldsValue({
                                            targetPosition: {
                                                ...form.getFieldValue('targetPosition'),
                                                y: Number(e.target.value)
                                            }
                                        })}
                                    />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    );
                case 'trial':
                    return (
                        <Form.Item
                            name="trial"
                            key="trial"
                            valuePropName="checked"
                        >
                            <Checkbox onChange={(e) => form.setFieldsValue({trial: e.target.checked})}>
                                Keine Ausführung {' '}
                                <span style={{fontSize: '12px', color: 'gray'}}>
                                   (Das Zielelement wird gesucht, ohne den Befehl darauf anzuwenden)
                                </span>
                            </Checkbox>
                        </Form.Item>
                    );
                case 'mouseButton':
                    return (
                        <Form.Item
                            name="mouseButton"
                            key="mouseButton"
                            label="Maustaste"
                        >
                            <Select
                                placeholder="Maustaste wählen"
                                allowClear
                                onChange={(value) => form.setFieldsValue({mouseButton: value})}
                            >
                                {mouseButtons.map((option) => (
                                    <Option key={option} value={option}>
                                        {option}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    );
                case 'clickCount':
                    return (
                        <Form.Item
                            name="clickCount"
                            key="clickCount"
                            label="Klickanzahl"
                        >
                            <Input
                                type="number"
                                placeholder="Anzahl der Klicks"
                                onChange={(e) => form.setFieldsValue({clickCount: Number(e.target.value)})}
                            />
                        </Form.Item>
                    );
                case 'delay':
                    return (
                        <Form.Item
                            name="delay"
                            key="delay"
                            label="Verzögerung (ms)"
                        >
                            <Input
                                type="text"
                                placeholder="Verzögerung in ms"
                                onChange={(e) => form.setFieldsValue({delay: Number(e.target.value)})}
                            />
                        </Form.Item>
                    );
                default:
                    return null;
            }
        });
    };

    return (
        <Collapse>
            <Panel header="Weitere Einstellungen (optional)" key="1">
                {renderInputFields()}
            </Panel>
        </Collapse>
    );
};

export default ExecuteCommandOptionComponent;
